"use client";

import { useState } from "react";
import { FiPlus } from "react-icons/fi";

import AddMeasurementsDialog from "@/components/measurements/add-measurements-dialog";
import MeasurementGroupCard from "@/components/patient/measurements/group-card";
import MeasurementRecordDialog from "@/components/patient/measurements/record-dialog";
import { useGroupedMeasurementData } from "@/components/patient/measurements/use-grouped-measurement-data";
import Button from "@/components/ui/button";
import CollapsibleSection from "@/components/ui/collapsible-section";
import { Skeleton } from "@/components/ui/skeleton";

import { isBodyGroup } from "@/lib/measurement-body";

import type { MeasurementGroupWithFields } from "@/types/measurement";
import type { PatientDetail } from "@/types/patient";

import { usePatientMeasurementData } from "@/query/patient/measurements";

function MeasurementsOverviewSkeleton() {
  return (
    <div className="space-y-4">
      {[1, 2].map((i) => (
        <div key={i} className="space-y-2">
          <Skeleton className="h-5 w-28" />
          <div className="grid grid-cols-2 gap-3">
            <Skeleton className="h-16 rounded-lg" />
            <Skeleton className="h-16 rounded-lg" />
          </div>
        </div>
      ))}
    </div>
  );
}

interface MeasurementsOverviewProps {
  patient: PatientDetail;
}

export default function MeasurementsOverview({
  patient,
}: MeasurementsOverviewProps) {
  const [recordGroup, setRecordGroup] =
    useState<MeasurementGroupWithFields | null>(null);

  const {
    groups,
    fields,
    values: allValues,
    isLoading,
  } = usePatientMeasurementData(patient.id);

  const {
    groupedData,
    rawGroupedData,
    groupsWithData,
    otherGroup,
    otherValuesByDate,
    rawOtherValuesByDate,
  } = useGroupedMeasurementData(groups, fields, allValues);

  const actions = (
    <AddMeasurementsDialog patientId={patient.id}>
      <Button variant="secondary" size="sm">
        <FiPlus className="mr-1 h-3 w-3" />
        Add
      </Button>
    </AddMeasurementsDialog>
  );

  return (
    <CollapsibleSection
      title="Measurements"
      persistKey="measurements-overview"
      actions={actions}
    >
      {isLoading ? (
        <MeasurementsOverviewSkeleton />
      ) : groupsWithData.length === 0 && !otherGroup ? (
        <div className="py-8 text-center">
          <p className="text-body text-muted-foreground">
            No measurements recorded
          </p>
        </div>
      ) : (
        <>
          <div>
            {groupsWithData.map((group) => (
              <MeasurementGroupCard
                key={group.id}
                group={group}
                valuesByDate={groupedData[group.id] ?? {}}
                rawValuesByDate={rawGroupedData[group.id] ?? {}}
                patientId={patient.id}
                onRecord={() => setRecordGroup(group)}
                variant="transparent"
                defaultDisplayType={isBodyGroup(group) ? "BODY" : "CARDS"}
              />
            ))}

            {otherGroup && (
              <MeasurementGroupCard
                group={otherGroup}
                valuesByDate={otherValuesByDate}
                rawValuesByDate={rawOtherValuesByDate}
                patientId={patient.id}
                onRecord={() => setRecordGroup(otherGroup)}
                variant="transparent"
                defaultDisplayType="CARDS"
                hideChartOption
              />
            )}
          </div>

          {recordGroup && (
            <MeasurementRecordDialog
              group={recordGroup}
              patientId={patient.id}
              isOpen={!!recordGroup}
              onClose={() => setRecordGroup(null)}
            />
          )}
        </>
      )}
    </CollapsibleSection>
  );
}
